import { useEffect, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { ApiError, apiPost } from '../api/client'

/**
 * "Enter Access Code", fereastra din sertarul rotitei.
 *
 * Markupul e cel din `application/views/version-3/_components/modals/accessCodeModal.php`
 * si purtarea e cea din `public/js/app/app.js` (blocul ACCESS CODE): opt casute
 * de cate o litera, cursorul sare singur inainte la scris si inapoi la stergere,
 * iar raspunsul se scrie sub ele, verde sau rosu.
 *
 * NU foloseste `Modal` din `ui/Modal.tsx`, desi mecanica e aceeasi: acolo
 * copiii intra in `.modal-body`, iar fereastra asta are un `.modal-footer`
 * FRATE cu corpul (butoanele Close si Validate). Pus in corp, subsolul si-ar
 * pierde linia de sus pe toata latimea si ar primi inca un rand de padding -
 * adica nu ar mai fi aceeasi fereastra ca in portal. Fazele lui bootstrap.js
 * (display:block, apoi `in`, si drumul invers) sunt scrise la fel ca acolo.
 *
 * Trimite la `POST /api/v2/user/pair_access_code` (`AccessCodeController`),
 * care leaga codul de omul logat aici; ecranul de intrare care l-a aratat
 * (`login/StepAccessCode.tsx`) primeste apoi sesiunea lui. Pana pe 11
 * septembrie 2026 trimitea in portalul vechi, care nu mai sta pe server.
 */

const DIGITS = 8

export function AccessCodeModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const [phase, setPhase] = useState<'hidden' | 'in' | 'leaving'>(open ? 'in' : 'hidden')
  const [code, setCode] = useState<string[]>(() => Array<string>(DIGITS).fill(''))
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const [sending, setSending] = useState(false)
  const boxes = useRef<Array<HTMLInputElement | null>>([])
  const everOpened = useRef(false)

  const shown = open || phase !== 'hidden'
  const inClass = open && phase === 'in'

  useEffect(() => {
    if (open) {
      everOpened.current = true
      const frame = requestAnimationFrame(() => setPhase('in'))
      return () => cancelAnimationFrame(frame)
    }
    /*
     * Cat n-a fost deschisa, nu are ce sa se inchida.
     *
     * Fara randul asta, fereastra intra pe drumul de inchidere chiar la prima
     * desenare: `phase` trece prin `leaving`, iar `shown` - care se uita la
     * "phase nu e hidden" - devine adevarat pentru 300 ms. Adica un val
     * intunecat peste tot antetul, la fiecare incarcare de pagina, pentru o
     * fereastra pe care nimeni n-a cerut-o.
     */
    if (!everOpened.current) return
    const frame = requestAnimationFrame(() => setPhase('leaving'))
    const timer = setTimeout(() => setPhase('hidden'), 300)
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [open])

  /* `shown.bs.modal` / `hidden.bs.modal` din app.js: focus pe prima casuta la
     deschidere, formular golit si mesaj sters la inchidere. */
  useEffect(() => {
    if (!open) {
      setCode(Array<string>(DIGITS).fill(''))
      setMessage(null)
      return
    }
    boxes.current[0]?.focus()
  }, [open])

  useEffect(() => {
    if (!shown) return
    document.body.classList.add('modal-open')
    const backdrop = document.createElement('div')
    backdrop.className = 'modal-backdrop fade'
    document.body.appendChild(backdrop)
    const frame = requestAnimationFrame(() => backdrop.classList.add('in'))
    return () => {
      cancelAnimationFrame(frame)
      backdrop.classList.remove('in')
      setTimeout(() => backdrop.remove(), 150)
      document.body.classList.remove('modal-open')
    }
  }, [shown])

  useEffect(() => {
    if (!open) return
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  /* Lipirea unui cod intreg intr-o casuta umple casutele care urmeaza, ca in
     app.js: prima litera ramane aici, restul se impinge mai departe. */
  const typeInto = (index: number, typed: string) => {
    setCode((old) => {
      const next = [...old]
      const letters = [...typed]
      for (let i = index; i < DIGITS && letters.length > 0; i += 1) {
        next[i] = letters.shift() ?? ''
      }
      return next
    })
    if (typed !== '' && index < DIGITS - 1) boxes.current[index + 1]?.focus()
  }

  const send = async (event: FormEvent) => {
    event.preventDefault()
    setSending(true)
    setMessage(null)
    try {
      await apiPost<{ status: string }>('/user/pair_access_code', { token: code.join('') })
      setCode(Array<string>(DIGITS).fill(''))
      setMessage({ ok: true, text: 'Code saved successfully.' })
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) {
        setMessage({ ok: false, text: error.message })
      } else {
        setMessage({ ok: false, text: 'An error occurred. Refresh the page or try again later.' })
      }
    } finally {
      setSending(false)
    }
  }

  /*
   * Fereastra se agata de <body>, nu de locul in care e scrisa.
   *
   * In portal, `accessCodeModal.php` se incarca din `_layout/footer.php`, adica
   * la radacina paginii; aici componenta traieste inauntrul unui `<li>` din
   * antet, iar o fereastra desenata acolo sta in cutia aceea - cu vecinii ei
   * care taie ce iese afara (`overflow`) si cu straturile lor. `createPortal` o
   * pune unde statea si inainte.
   */
  return createPortal(
    <div
      className={`modal fade${inClass ? ' in' : ''}`}
      id="accessCodeModal"
      tabIndex={-1}
      role="dialog"
      aria-labelledby="accessCodeModalLabel"
      aria-hidden={shown ? undefined : 'true'}
      style={shown ? { display: 'block' } : undefined}
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose()
      }}
    >
      <div className="modal-dialog" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <button type="button" className="close" aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
            <h4 className="modal-title" id="accessCodeModalLabel">
              Enter access code
            </h4>
          </div>
          <form id="reedem_access_code" onSubmit={(ev) => void send(ev)}>
            <div className="modal-body">
              <h4 className="text-center color-light-grey mt-2 font-14 font-weight-bold">
                Enter your access code to assign a new device
              </h4>
              <div className="number-code px-5 mt-3 mb-3">
                <div>
                  {code.map((value, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        boxes.current[index] = el
                      }}
                      type="text"
                      className="code-input"
                      aria-label={`Access code, character ${index + 1}`}
                      required
                      value={value}
                      onChange={(ev) => typeInto(index, ev.target.value)}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Backspace' && value === '') boxes.current[Math.max(0, index - 1)]?.focus()
                      }}
                    />
                  ))}
                </div>
              </div>
              <div id="access-code-msg">
                {message !== null && (
                  <div className={`alert ${message.ok ? 'alert-success' : 'alert-danger'} mt-2 mb-0`}>
                    <i className={message.ok ? 'far fa-check-circle' : 'fas fa-exclamation-triangle'} /> {message.text}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <div className=" d-flex justify-content-between">
                <button type="button" className="btn btn-default m-0" onClick={onClose}>
                  Close
                </button>
                <button type="submit" className="btn btn-primary" disabled={sending}>
                  Validate
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body,
  )
}
